import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { identify } from '@lf/analytics-events';
import { initializeAnalytics, setUpAnalytics, usePageTracking } from './analytics';
import './App.css';
import { ErrorProvider } from './contexts/ErrorContext';
import { HeaderProvider } from './contexts/HeaderContext';
import { ProfileProvider } from './contexts/ProfileContext';
import ErrorNotification from './components/ErrorNotification';
import VideosPage from './pages/VideosPage';
import VideoPage from './pages/VideoPage';
import HomePage from './pages/HomePage';
import StudyPlanPage from './pages/StudyPlanPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import yted from '@lf/api-client/lfApi';
import Header from './components/Header';
import TrialBanner from './components/TrialBanner';
import StudentSubscribePrompt from './cards/StudentSubscribePrompt';
import { isStudentPaywalled } from './lib/subscription/paywall';
import ChildSignupPage from './pages/ChildSignupPage';
import ChildAgeCheckPage from './pages/ChildAgeCheckPage';
import ShareLandingPage from './pages/ShareLandingPage';
import { getStudyPlans } from '@lf/api-client/lfApiStudyPlan';
import { readPendingShareToken } from './lib/share/pendingShare';

async function getChildProfile() {
  try {
    const response = await yted.get('/child/profile', {
      timeout: 5000,
    });
    if (response.status === 200) {
      return response.data;
    }
    else {
      console.error('Failed to fetch child profile');
      return null;
    }
  }
  catch (error) {
    console.error('Failed to fetch child profile', error);
    return null;
  }
}

function App() {
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [score, setScore] = useState(0);
  const [studyPlans, setStudyPlans] = useState([]);
  const [subscriptionAction, setSubscriptionAction] = useState({ action: 'none' });
  const [hasCompletedVideos, setHasCompletedVideos] = useState(false);
  const [trial, setTrial] = useState(null);
  const [isLinked, setIsLinked] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const fetchAndSetProfile = async (updateProfile = true) => {
    const profileData = await getChildProfile();
    if (profileData && profileData.profile) {
      if (updateProfile)
        setProfile(profileData.profile);

      setUser(profileData.user);
      setScore(profileData.score);
      setTrial(profileData.trial || null);
      setIsLinked(profileData.is_linked || false);
    }
  };

  const setSettingsProfile = (profile) => {
    if (profile) {
      setProfile(profile);
    } else {
      setProfile(null);
      setUser(null);
      setScore(0);
    }
  };

  const setupAnalyticsWithProfile = useCallback(() => {
    if (profile) {
      setUpAnalytics(profile);
    }
  }, [profile]);

  const fetchAndSetStudyPlans = useCallback(async (isArchived = false) => {
    const sps = await getStudyPlans(profile?.username, isArchived, null);
    if (sps) {
      setStudyPlans(sps.study_plans);
      setSubscriptionAction(sps.subscription_action);
      setHasCompletedVideos(Object.keys(sps.completed_video_ids || {}).length > 0);
    }
  }, [profile]);

  const onStudyPlan = (studyPlanId) => {
    navigate(`/feed?sp=${studyPlanId}`);
  };

  const onChildProfile = () => {
    navigate('/settings');
  };

  const onAddStudyPlan = () => {
    navigate('/settings/add_study_plan');
  };

  useEffect(() => {
    initializeAnalytics();
    fetchAndSetProfile();

    // Per design §C: clear identity on tab close so a second child on
    // the same browser doesn't inherit the prior user_id.
    const onPageHide = () => identify(null, {});
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  useEffect(() => {
    if (profile) {
      setupAnalyticsWithProfile();
      fetchAndSetStudyPlans();
    }
  }, [fetchAndSetStudyPlans, profile, setupAnalyticsWithProfile]);

  useEffect(() => {
    if (!profile) {
      return;
    }

    if (location.pathname === '/') {
      // Signed-in users arriving with ?share=<token> (e.g. clicking
      // "Import using student app" on the parent app) should land on
      // the import page, not /home.
      const shareToken = readPendingShareToken();
      if (shareToken) {
        navigate(`/share/${shareToken}`);
      } else {
        navigate('/home');
      }
    }
  }, [location.pathname, navigate, profile]);

  usePageTracking();

  const profileContextValue = {
    profile,
    user,
    score,
    trial,
    isLinked,
    fetchProfile: fetchAndSetProfile,
    setProfile: setSettingsProfile,
  };

  return (
    <div className="App flex flex-col h-screen">
      <ProfileProvider value={profileContextValue}>
      <HeaderProvider>
        <Header
          childProfile={profile}
          score={score}
          studyPlans={studyPlans}
          onChildProfile={onChildProfile}
          onStudyPlan={onStudyPlan}
          onAddStudyPlan={onAddStudyPlan} />

        <main className="flex-1 p-4 w-full mx-auto max-w-screen-lg">
          <ErrorProvider>
            {profile && isStudentPaywalled({ trial, isLinked, subscriptionAction }) ? (
              // Hard gate: a spent trial / unresolved subscription replaces the
              // app with a dedicated paywall page so the student can focus on
              // resolving billing, not a banner stacked above the feed.
              <div className="flex justify-center pt-4">
                <StudentSubscribePrompt subscriptionAction={subscriptionAction} isLinked={isLinked} />
              </div>
            ) : (
            <>
            {profile && <TrialBanner trial={trial} isLinked={isLinked} subscriptionAction={subscriptionAction} />}
            <Routes>
              <Route path="/share/:token" element={
                <ShareLandingPage
                  fetchStudyPlans={fetchAndSetStudyPlans} />
              } />
              <Route path="/signup" element={
                <ChildSignupPage />
              } />
              <Route path="/age-check" element={
                <ChildAgeCheckPage />
              } />
              <Route path="/" element={
                profile ? (
                  <VideosPage profile={profile} studyPlans={studyPlans} subscriptionAction={subscriptionAction} isLinked={isLinked} />
                ) : (
                  <LoginPage />
                )
              } />
              <Route path="/feed" element={
                profile ? (
                  <VideosPage profile={profile} studyPlans={studyPlans} subscriptionAction={subscriptionAction} isLinked={isLinked} />
                ) : (
                  <LoginPage />
                )
              } />
              <Route path="/youtube" element={
                profile ? (
                  <HomePage disableInteractiveFeatures />
                ) : (
                  <LoginPage />
                )
              } />
              <Route path="/home" element={
                profile ? (
                  <HomePage />
                ) : (
                  <LoginPage />
                )
              } />
              <Route path="/video" element={
                profile ? (
                  <VideoPage hasCompletedVideos={hasCompletedVideos} />
                ) : (
                  <LoginPage />
                )
              } />
              <Route path="/studyPlan/*" element={
                <StudyPlanPage />
              } />
              <Route path="/settings/*" element={
                <SettingsPage
                  profile={profile}
                  setProfile={setSettingsProfile}
                  studyPlans={studyPlans}
                  setStudyPlans={setStudyPlans}
                  fetchStudyPlans={fetchAndSetStudyPlans}
                  setSubscriptionAction={setSubscriptionAction}
                />
              } />
              <Route path="/profile/*" element={
                <ProfilePage />
              } />
            </Routes>
            </>
            )}
            <ErrorNotification />
          </ErrorProvider>
        </main>

        <footer className="bg-branch text-white text-center p-4 mt-12">
          <p>
            &copy; 2026 Learnforest
          </p>
          <p className="text-xs mt-1">
            <a href="https://learnforest.app/tos.html" target="_blank" rel="noreferrer">Terms of Service</a>
            <span className="mx-2">|</span>
            <a href="https://learnforest.app/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>
            <span className="mx-2">|</span>
            <a href="mailto:support@learnforest.app">Support</a>
          </p>
        </footer>
      </HeaderProvider>
      </ProfileProvider>
    </div>
  );
}

export default App;
